import { TopBarAction } from '@/components/ui/TopBar'
import { TopBarActionId } from '@/components/ui/TopBarActionIds'
import { useTooltip } from '@/hooks/useTooltip'
import { useSetWorkflowBookmarkMutation } from '@/lib/features/workflow/workflowApi'
import { Download, PanelRight, Tag } from 'lucide-react'
import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { FiHeart } from 'react-icons/fi'
import { toast } from 'react-toastify'

export interface UseWorkflowActionsProps {
  mode: 'results' | 'workflow'
  // Details button props (only for workflow mode)
  isDetailsVisible?: boolean
  detailsMode?: string
  handleDetailsToggle?: () => void
  // Admin export props
  workflowRef?: string
  isAdmin?: boolean
  // Tag management props
  onOpenTags?: () => void
  currentTags?: Array<{ id: string; name: string }>
  // Bookmark props
  workflowId?: string
  isBookmarked?: boolean
}

// Return type is now an array of actions
export type WorkflowActionsReturn = TopBarAction[]

export function useWorkflowActions({
  mode,
  isDetailsVisible,
  detailsMode,
  handleDetailsToggle,
  workflowRef,
  isAdmin = false,
  onOpenTags,
  currentTags = [],
  workflowId,
  isBookmarked = false,
}: UseWorkflowActionsProps): WorkflowActionsReturn {
  return useMemo(() => {
    const actions: TopBarAction[] = []

    // Add details action only in workflow mode
    if (
      mode === 'workflow' &&
      handleDetailsToggle &&
      isDetailsVisible !== undefined &&
      detailsMode
    ) {
      actions.push({
        id: TopBarActionId.WORKFLOW_DETAILS,
        element: (
          <DetailsButton
            isVisible={isDetailsVisible}
            mode={detailsMode}
            onClick={handleDetailsToggle}
          />
        ),
        visible: true,
        order: 1,
      })
    }

    // Add bookmark action in workflow mode
    if (mode === 'workflow' && workflowId) {
      actions.push({
        id: TopBarActionId.WORKFLOW_BOOKMARK,
        element: (
          <BookmarkButton
            workflowId={workflowId}
            workflowRef={workflowRef}
            isBookmarked={isBookmarked}
          />
        ),
        visible: true,
        order: 2,
      })
    }

    // Add tags action in workflow mode
    if (mode === 'workflow' && onOpenTags) {
      actions.push({
        id: TopBarActionId.WORKFLOW_TAGS,
        element: <TagsButton onClick={onOpenTags} tagCount={currentTags.length} />,
        visible: true,
        order: 3,
      })
    }

    // Add admin export action
    if (isAdmin && workflowRef && mode === 'workflow') {
      actions.push({
        id: TopBarActionId.WORKFLOW_EXPORT_TEMPLATE,
        element: <ExportTemplateButton workflowRef={workflowRef} />,
        visible: true,
        order: 10,
      })
    }

    return actions
  }, [
    mode,
    isDetailsVisible,
    detailsMode,
    handleDetailsToggle,
    workflowRef,
    isAdmin,
    onOpenTags,
    currentTags.length,
    workflowId,
    isBookmarked,
  ])
}

// Custom Details Button Component
function DetailsButton({
  isVisible,
  mode,
  onClick,
}: {
  isVisible: boolean
  mode: string
  onClick: () => void
}) {
  const [isHovered, setIsHovered] = useState(false)
  const isAutoMode = mode.startsWith('auto')

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
        isVisible
          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
      title={`${isVisible ? 'Hide' : 'Show'} details panel - ${isAutoMode ? 'Auto' : 'Manual'}`}
    >
      {isAutoMode && isHovered ? (
        // Show "A" when hovering over auto mode
        <span className="text-sm font-bold">A</span>
      ) : (
        <PanelRight size={16} />
      )}
    </button>
  )
}

// Custom Bookmark Button Component
function BookmarkButton({
  workflowId,
  workflowRef,
  isBookmarked,
}: {
  workflowId: string
  workflowRef?: string
  isBookmarked: boolean
}) {
  const [setBookmark, { isLoading }] = useSetWorkflowBookmarkMutation()
  const { showTooltip, tooltipPosition, buttonRef, showTooltipWithDelay, hideTooltip } =
    useTooltip()

  const handleToggleBookmark = async () => {
    try {
      await setBookmark({ workflowId, workflowRef, bookmarked: !isBookmarked }).unwrap()
      toast.success(isBookmarked ? 'Removed from favorites' : 'Added to favorites')
    } catch (error) {
      console.error('Failed to toggle bookmark:', error)
      toast.error('Failed to update bookmark')
    }
  }

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggleBookmark}
        onMouseEnter={showTooltipWithDelay}
        onMouseLeave={hideTooltip}
        disabled={isLoading}
        className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
          isBookmarked
            ? 'bg-red-100 text-red-600 hover:bg-red-200'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        } ${isLoading ? 'cursor-not-allowed opacity-60' : ''}`}
      >
        {isLoading ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          <FiHeart size={16} fill={isBookmarked ? 'currentColor' : 'none'} />
        )}
      </button>
      {showTooltip &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="font-hanken-grotesk pointer-events-none fixed z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            {isBookmarked ? 'Unfavorite' : 'Favorite'}
          </div>,
          document.body
        )}
    </>
  )
}

// Custom Tags Button Component
function TagsButton({ onClick }: { onClick: () => void; tagCount: number }) {
  const { showTooltip, tooltipPosition, buttonRef, showTooltipWithDelay, hideTooltip } =
    useTooltip()

  return (
    <>
      <button
        ref={buttonRef}
        onClick={onClick}
        onMouseEnter={showTooltipWithDelay}
        onMouseLeave={hideTooltip}
        className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200"
      >
        <Tag size={16} />
      </button>
      {showTooltip &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="font-hanken-grotesk pointer-events-none fixed z-[9999] -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            Manage tags
          </div>,
          document.body
        )}
    </>
  )
}

// Custom Export Template Button Component (Admin only)
function ExportTemplateButton({ workflowRef }: { workflowRef: string }) {
  const [isExporting, setIsExporting] = useState(false)

  const handleExportTemplate = async () => {
    setIsExporting(true)
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/templates/export`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ workflowRef }),
        }
      )

      if (!response.ok) {
        console.error('Export failed - Status:', response.status, response.statusText)
        const errorText = await response.text()
        console.error('Export failed - Response:', errorText)
        throw new Error(`Export failed: ${response.status} ${response.statusText}`)
      }

      const { template, filename } = await response.json()

      // Download the template JSON file
      const blob = new Blob([JSON.stringify(template, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export template:', error)
      toast.error('Failed to export template: ' + error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <button
      onClick={handleExportTemplate}
      disabled={isExporting}
      className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
        isExporting
          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
          : 'bg-green-100 text-green-700 hover:bg-green-200'
      }`}
      title="Export as Template (Admin)"
    >
      {isExporting ? (
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-green-700 border-t-transparent" />
      ) : (
        <Download size={16} />
      )}
    </button>
  )
}
